import { useCallback, useState } from "react";

import {
  useProjectOverviewQuery,
  type LifecycleRefusalReason,
  type ProjectLifecycle,
} from "@/api/hooks";
import {
  useCandidateContentsQuery,
  type CandidateSummary,
} from "@/api/preview";
import {
  classifyPublicationFailure,
  classifySignoffFailure,
  useCreatePublicationMutation,
  useCreateSignoffMutation,
  type ClinicianPublicationStatus,
  type PublicationFailure,
  type SignoffFailure,
} from "@/api/publication";
import { useSession } from "@/auth/SessionContext";
import {
  clearIdempotencyKey,
  publicationRequestFor,
} from "@/publishing/publicationRequest";
import { Button } from "@/ui/Button";
import { supersessionSentence } from "@/lib/candidateFreshness";
import { formatDateTime } from "@/lib/format";
import { lifecycleRefusalCopy } from "@/lib/lifecycle";

import { ApprovalProgress } from "./ApprovalProgress";
import { CandidateAcceptanceDetail } from "./CandidateAcceptanceDetail";
import { CandidateReview } from "./CandidateReview";
import { PublicationStatusView } from "./PublicationStatus";

/**
 * The clinician's publication lane on the Project page (#9, completed by #445).
 *
 * Two actions, each offered only at the rung where it is actually callable:
 * **Accept** records clinician sign-off on the exact candidate that was
 * previewed (#20), and **Request publication** hands that accepted candidate to
 * the service, which publishes it as the ENVIRONMENT's GitHub App installation:
 * a requester-attributed branch and draft overlay PR opened by the App, with
 * this clinician's Entra identity recorded on the row and in the PR (#48, App
 * identity). Session + Editor role is the whole precondition — no GitHub
 * account is connected, refreshed or reconnected anywhere in this lane.
 *
 * THE LATER RUNGS NOW EXIST. This header used to say the note-and-PDF handoff
 * "is a later rung and a later ticket, and is deliberately not stubbed here",
 * because the handoff binds an `approvalId` that only exists once a release
 * owner has approved externally, and nothing could read one. #444 published that
 * evidence and #445 spends the note: `ApprovalProgress` below renders the
 * approval reading and, on a reading the handoff would accept right now, offers
 * the note-and-document step. What has NOT changed is why the modal was refused
 * before — it is still offered only where the contract can honour it, never
 * stubbed ahead of one.
 *
 * SINCE #435 IT ALSO SHOWS WHAT IS BEING SIGNED FOR. `CandidateReview` renders
 * the frozen candidate's own contents, from `GET .../contents` and from nothing
 * else, and the acceptance request is composed from that same response — so the
 * identity displayed and the identity signed are one value rather than two that
 * agree most of the time. Acceptance is withheld until that response has
 * arrived, and withheld again the moment the project's latest candidate moves
 * past the one on screen: the prop below re-renders on every overview refetch,
 * which used to swap the acceptance target underneath a rendered inspection
 * without saying so.
 *
 * AND FRESHNESS IS ESTABLISHED AT THE CLICK, not remembered from the last
 * render. Nothing re-runs the Project Overview query on its own — it carries no
 * interval and no subscription, and this application disables focus refetching
 * — so a colleague freezing a newer version while this browser sits on the page
 * moves nothing here, and a comparison against the last cached prop would
 * quietly report "current" for as long as the tab stayed open. `handleAccept`
 * therefore re-reads the Project's current candidate immediately before it
 * posts, and fails closed on BOTH branches: a newer candidate refuses the
 * acceptance and shows the stale notice, and a re-read that did not answer
 * refuses it too and says the version could not be confirmed current. What is
 * still not closed is the window between that answer and the write — see the
 * comment on `handleAccept`.
 *
 * The status above the actions is the authoritative one, read from
 * `ProjectOverview.publicationStatus`. Nothing here recomputes it from the
 * outcome of a mutation: a successful accept invalidates the overview and the
 * server answers again. A surface that optimistically painted "Accepted" would
 * be asserting a clinical fact the Editor had not yet stored.
 *
 * WHICH CONTROL IS OFFERED is a different question from what the status SAYS,
 * and the two are decided separately. The status is the server's sentence,
 * rendered verbatim; the control is whatever that sentence's own recovery
 * requires, which for `action_needed` depends on the `reason` — see
 * `OFFERS_PUBLICATION`. Gating the publication control on the compact state
 * alone left two of the three recovery sentences describing an action the
 * surface withheld.
 *
 * THE RESUME IS GONE WITH THE ROUND TRIP. #47 used to persist the intent and
 * navigate to GitHub for the clinician's own authorization, and this lane
 * replayed the stored request on return. Publication no longer leaves the
 * page: the request is a single POST, and what survives for a retry is the
 * idempotency key `publishing/publicationRequest.ts` keeps per candidate.
 *
 * NOT a publication dashboard. #203 forbids one and
 * `invariants/projectUxAmendment.test.ts` fails the build if one appears. The
 * technical coordinates stay where they already are — secondary, on the
 * Promotion card — and there is no publishing-account surface to keep off this
 * page any more: the App identity retired it everywhere.
 */

const ELIGIBILITY_COPY = {
  awaiting_ratified_guard_catalog:
    "This version was frozen before the approved safety-rule catalogue was in place, so it cannot be accepted for publication. Freeze a new version once the catalogue is available.",
  awaiting_verified_engine_identity:
    "This version was validated by an engine build that has not been verified, so it cannot be accepted for publication. Freeze a new version once validation reports a verified engine.",
} satisfies Record<
  Extract<CandidateSummary["eligibility"], { state: "preview_only" }>["reason"],
  string
>;

export const SIGNOFF_FAILURE_COPY = {
  stale_base:
    "This Project's preview target now runs a different baseline than this Project started from. Refresh the baseline on the Project overview, review, then accept.",
  not_evidence_eligible:
    "This version cannot be accepted for publication. Freeze a new version once the approved safety-rule catalogue is in place.",
  no_preview_evidence:
    "Preview this version as a patient first, against this Project's current preview target, then accept it. A change of preview target also means previewing again.",
  denied: "You are not authorized to accept a version for this Project.",
  unavailable: "Accepting a version is not available in this environment yet.",
  error: "The version could not be accepted. Try again.",
} satisfies Record<SignoffFailure["kind"], string>;

const PUBLICATION_FAILURE_COPY = {
  not_evidence_eligible:
    "This version cannot be published. Freeze a new version once the approved safety-rule catalogue is in place.",
  head_moved:
    "This version moved since it was accepted. Review the content and accept it again before requesting publication.",
  idempotency_conflict:
    "A different publication was already requested with this attempt. Reload the page and try again.",
  // The service's own publishing identity is down or refused — never the
  // clinician's account, which publication no longer involves. The one
  // sentence that matters comes first: nothing about the accepted version
  // changed, and a retry replays the same request.
  publishing_unavailable:
    "The publishing service is unavailable. Your accepted version is safe; try again later or contact an administrator.",
  publishing_refused:
    "GitHub refused the publishing service's request. Your accepted version is safe; an administrator needs to check the publishing service's access before this can be retried.",
  denied: "You are not authorized to request publication for this Project.",
  unavailable:
    "Requesting publication is not available in this environment yet.",
  error: "Publication could not be requested. Try again.",
} satisfies Record<PublicationFailure["kind"], string>;

// `OFFERS_PUBLICATION` stood here — a browser policy table mapping each
// `action_needed` reason to whether **Request publication** was offered — and
// #651 retired it for `lifecycle.capabilities.requestPublication` (#650): the
// SERVER's per-candidate verdict, derived from the same rows the write path
// refuses on. The table had already drifted from the write once: it offered a
// republish while the same candidate's publication sat on a red automated
// review, an action `github/publication` refuses as in progress — exactly the
// re-derived-policy divergence #650's verdicts exist to end. The reasoning the
// table carried (a `failed` attempt must not block the retry; acceptance of
// the exact current candidate unlocks publication; a trial row is never an
// acceptance proxy) now lives in the verdict's own derivation, tested against
// the write it predicts.

/**
 * Why the acceptance action is refusing, when the version itself is fine.
 *
 * Separate from `SIGNOFF_FAILURE_COPY` above, which explains a request the
 * service refused. These explain a request this surface will not make yet, and
 * each of them corresponds to a control that is rendered and visibly disabled
 * rather than hidden: a clinician who came here to accept a version needs to
 * read why they cannot, and a control that simply vanished would tell them
 * nothing. (`Button` expresses disabled as `aria-disabled` and stays focusable
 * for the same reason.)
 *
 * `stale` deliberately says what to do and does not do it. Moving the review to
 * the newer version is one click away below, and it has to be the clinician's
 * click: silently re-pointing a rendered inspection at a version nobody has
 * read is the exact defect #435 exists to remove.
 */
const ACCEPTANCE_BLOCKED_COPY = {
  loading: "Read the frozen version above before accepting it.",
  unreadable:
    "This version cannot be accepted until its content can be shown above.",
  stale:
    "A newer version has been frozen for this project since this one was opened. Read the newer version before accepting anything.",
} as const;

/**
 * What a refused acceptance says when the freshness check itself did not answer.
 *
 * Separate from everything above because it describes an action that was TAKEN
 * and did not go through, rather than one the surface is withholding — and it
 * has to say that first: the clinician pressed Accept, and the one thing they
 * must not be left believing is that a sign-off was recorded.
 *
 * The control stays live beneath it, deliberately. This is the transient arm —
 * an unreachable service or a lost connection, since an expired session takes
 * the whole page and a denial would have taken the Project long before this
 * lane — so pressing Accept again IS the recovery, and it re-runs the same
 * confirmation before it would post anything. A control disabled with no way
 * back would be the dead-end this pull request removed elsewhere.
 */
const FRESHNESS_UNCONFIRMED_COPY =
  "This version was not accepted. The project's current version could not be read just now, so there is no way to confirm this is still it. Try accepting it again.";

export function PublicationLane({
  projectId,
  status,
  candidate = null,
  lifecycle,
  detailsOnly = false,
}: {
  projectId: string;
  detailsOnly?: boolean;
  status: ClinicianPublicationStatus;
  candidate?: CandidateSummary | null;
  /**
   * #650's canonical reading, from the same Overview response as `status` —
   * one database instant. The verdicts gate the two actions here; the
   * candidate block carries the server-derived revision number and validity
   * window this lane renders instead of inventing either.
   */
  lifecycle: ProjectLifecycle;
}) {
  const signoff = useCreateSignoffMutation(projectId);
  const publication = useCreatePublicationMutation(projectId);
  // Self-attribution only, and read from the SESSION the service issued rather
  // than from the auth adapter's cached account: `acceptedBy` is the subject
  // the service recorded for the caller, so the session's subject is the one
  // value the two sides are guaranteed to agree on.
  const { session } = useSession();

  /**
   * The candidate this lane is INSPECTING, which is not the same question as
   * which candidate the project's latest is (#435).
   *
   * `candidate` is `ProjectOverview.draft.latestCandidate`, a prop that
   * re-renders every time the overview query refetches — so a colleague
   * freezing a newer version while this one is being read moves it underneath a
   * rendered inspection. Before this pin, the acceptance action followed it
   * silently: the clinician read A and the button sent B.
   *
   * Pinned once and then only ever moved by the clinician's own click, with one
   * exception below: adoption when nothing is pinned yet. That is not a
   * retarget — there is no rendered inspection to protect — it is the first
   * candidate this lane has ever been given, arriving a tick after mount
   * because the overview had not resolved.
   */
  const [inspectedCandidateId, setInspectedCandidateId] = useState<
    string | null
  >(candidate?.candidateId ?? null);
  if (inspectedCandidateId === null && candidate !== null) {
    // Adjusting state during render rather than in an effect: React re-renders
    // this component before touching the DOM, so the first paint already shows
    // the right candidate instead of a frame of "nothing pinned".
    setInspectedCandidateId(candidate.candidateId);
  }

  /**
   * The frozen contents of the pinned candidate, and the SINGLE source of the
   * identity that both the review surface renders and the acceptance request
   * carries.
   *
   * Held here rather than inside `CandidateReview` precisely so the two cannot
   * disagree: `handleAccept` below composes its request from this object, and
   * the panel renders the same object. One value, read twice.
   */
  const contents = useCandidateContentsQuery(projectId, inspectedCandidateId);

  /**
   * The Project Overview, observed here so this lane can RE-READ the project's
   * current candidate rather than only be handed whatever it last was.
   *
   * The same query the route reads the `candidate` prop from, so this is one
   * cache entry with two observers rather than a second source of the same
   * fact: TanStack Query hands both the identical object, and a refetch either
   * observer triggers settles for both in the same commit. The prop stays for
   * everything it already did; what it cannot do, and the reason this is here,
   * is answer "is that still true?" at the moment acceptance is pressed.
   */
  const overview = useProjectOverviewQuery(projectId);

  const [signoffFailure, setSignoffFailure] = useState<SignoffFailure | null>(
    null,
  );
  /**
   * An acceptance that was pressed and refused because freshness could not be
   * established. Cleared by the next attempt, so it describes the last one.
   */
  const [freshnessUnconfirmed, setFreshnessUnconfirmed] = useState(false);
  /**
   * An acceptance that was pressed and refused by the FRESH verdict — the
   * click-time re-read answered, and its `acceptCandidate` said refused. The
   * refusal is the server's stable reason, rendered in its ratified sentence,
   * and nothing was posted: the same fail-closed answer the write would have
   * given, one round trip earlier. Cleared by the next attempt.
   */
  const [verdictRefusal, setVerdictRefusal] =
    useState<LifecycleRefusalReason | null>(null);
  /** The freshness re-read is in flight; the acceptance has not been sent. */
  const [confirming, setConfirming] = useState(false);
  const [publicationFailure, setPublicationFailure] =
    useState<PublicationFailure | null>(null);
  const recordPublicationFailure = useCallback((error: unknown) => {
    setPublicationFailure(classifyPublicationFailure(error));
  }, []);

  /**
   * The version that was actually READ, and is on screen right now, or nothing.
   *
   * Three conditions, each fail-closed, and each matching a branch
   * `CandidateReview` refuses to render content on — the two must agree,
   * because a panel showing a failure above an enabled Accept button is worse
   * than either state on its own.
   *
   *  - `isError` is checked even though `data` may still be cached. A freeze
   *    invalidates this query's prefix, so an unrelated freeze can trigger a
   *    re-read of THIS candidate that fails; TanStack Query then reports the
   *    error while keeping the last good response. The bytes were verified when
   *    they arrived and the candidate is immutable, so serving them on is
   *    tempting — but a 503 from this route can also mean the stored archive no
   *    longer verifies, and accepting a version the service has just declined
   *    to stand behind is the one outcome worth being conservative about. The
   *    retry in the panel restores it in one click.
   *  - `data` present is what "loaded" means.
   *  - the identity comparison refuses a response about another candidate.
   */
  const reviewed =
    !contents.isError &&
    contents.data &&
    contents.data.candidateId === inspectedCandidateId
      ? contents.data
      : null;

  /**
   * The project's current candidate, from the freshest read this lane has.
   *
   * `overview.data` and the `candidate` prop are the same cache entry, so in
   * production they are the same object; the query is preferred anyway because
   * it is the one this component can refresh, and after `handleAccept`'s
   * re-read it is guaranteed to be the newer of the two. The prop is the
   * fallback for a caller that has not resolved the overview — the pinning
   * seed's own case — rather than a second opinion.
   */
  const currentCandidate = overview.data?.draft.latestCandidate ?? candidate;

  /**
   * The project's latest candidate has moved past the one on screen.
   *
   * Client-side by necessity: the contents route answers about one immutable
   * candidate and has no stale reading to give. This comparison is the only
   * thing standing between "the clinician read this version" and "the clinician
   * signed for whichever version was newest when they clicked".
   *
   * It is a BACKGROUND signal, and on its own it was the blocking defect: this
   * value is only as fresh as the last overview read, and nothing schedules
   * one. It says so as soon as something else refreshes the overview — a
   * freeze from this browser, a navigation, the acceptance re-read below — and
   * `handleAccept` no longer trusts it to be the whole guard.
   */
  const stale =
    currentCandidate !== null &&
    inspectedCandidateId !== null &&
    currentCandidate.candidateId !== inspectedCandidateId;

  /**
   * The two verdicts (#650), each about the project's CURRENT candidate — the
   * same candidate the acceptance snapshot describes, from the same response.
   */
  const acceptVerdict = lifecycle.capabilities.acceptCandidate;
  const publishVerdict = lifecycle.capabilities.requestPublication;
  /**
   * The accept verdict's refusal, read only while the candidate on screen IS
   * the current one: the verdict describes the current candidate, and when
   * the two differ, staleness is the sentence that matters.
   */
  const verdictReason =
    stale || acceptVerdict.state !== "refused" ? null : acceptVerdict.reason;
  /**
   * The verdict refusals that DISABLE acceptance, with their sentence. Only
   * the ones that cannot flip underneath this page by a background event: an
   * expiry never un-expires for a candidate, and authorization does not come
   * back mid-session. `no_qualifying_preview` is deliberately NOT here — the
   * qualifying load lands seconds AFTER a preview tab opens, so the cached
   * verdict can lag the truth; that reason renders as guidance while the
   * control stays live, and the click-time re-read in `handleAccept` decides
   * with fresh eyes. Eligibility refusals keep their richer per-reason
   * sentence (`ELIGIBILITY_COPY`) and are not restated from the verdict.
   */
  const hardVerdictRefusal =
    verdictReason === "lifecycle.candidate_expired" ||
    verdictReason === "lifecycle.capability_missing" ||
    verdictReason === "lifecycle.project_archived"
      ? verdictReason
      : null;

  /**
   * Acceptance sends the identity that was DISPLAYED, and no other — after
   * establishing, at this click, that it is still the project's current one.
   *
   * Every field of the request comes off `reviewed` — the object the panel
   * above rendered — rather than off the `candidate` prop, which is the
   * project's current latest and may by now be a different version entirely.
   * The guard is not defensive tidiness: it is the acceptance criterion, since
   * an acceptance row is the evidence a clinician read this exact frozen
   * version and takes responsibility for it.
   *
   * ## Why the re-read exists
   *
   * `stale` above compares against the last overview READ, and nothing renews
   * it: `useProjectOverviewQuery` has no `refetchInterval` and no subscription,
   * `createQueryClient` sets `refetchOnWindowFocus: false`, and an expired
   * stale time does not re-run a mounted query on its own. So a colleague can
   * freeze B while this browser reads A, and the prop, the comparison and the
   * enabled control all stay on A for as long as the tab is open. The refetch
   * below is what turns "nothing has told us otherwise" into "we asked".
   *
   * ## Both refusals are refusals
   *
   * A newer candidate refuses the acceptance — and does not retarget it: the
   * refreshed read is what makes `stale` true, and the notice above then offers
   * the one control that moves the review, which stays the clinician's click.
   * A re-read that did not ANSWER refuses it as well, because "we could not
   * ask" is not "we asked and it was current", and TanStack Query keeps the
   * last good body on an errored refetch — so reading `data` without checking
   * `isError` first would turn the failure back into the cached "current" this
   * whole path exists to stop trusting.
   *
   * ## What it does NOT close, deliberately
   *
   * A browser re-read narrows the race to one round trip; it cannot remove it.
   * `POST /v1/projects/{projectId}/signoffs` guards the exact candidate tuple
   * and the Project's current preview-target revision, but nothing in it
   * requires the accepted candidate to still be the Project's LATEST — so a
   * freeze landing between the answer below and the write below still commits.
   * Closing that needs a server-side current-candidate predicate on #20's
   * contract, which #435's delivery guard puts outside this ticket: the gap is
   * documented on #430 instead, and named in this pull request's description.
   */
  async function handleAccept() {
    if (
      !reviewed ||
      stale ||
      signoff.isPending ||
      confirming ||
      hardVerdictRefusal !== null
    ) {
      return;
    }
    setSignoffFailure(null);
    setFreshnessUnconfirmed(false);
    setVerdictRefusal(null);

    setConfirming(true);
    // `.catch` as well as the `isError` branch: a refetch normally RESOLVES
    // with an errored result, but `throwOnError` can make it reject, and an
    // unconfirmed acceptance must fail closed rather than escape as a rejected
    // promise nobody handles.
    const confirmation = await overview.refetch().catch(() => null);
    setConfirming(false);

    if (confirmation === null || confirmation.isError || !confirmation.data) {
      setFreshnessUnconfirmed(true);
      return;
    }

    const current = confirmation.data.draft.latestCandidate;
    if (!current || current.candidateId !== reviewed.candidateId) {
      // Nothing is said here: the refreshed read has already made `stale` true,
      // and the notice above is the sentence, with the control that acts on it.
      // A project reporting no current candidate at all lands here too — it is
      // equally not a confirmation that this one is current.
      return;
    }

    // The FRESH verdict (#650/#651), from the same re-read that just proved
    // currency: where it says refused, the write would refuse with the same
    // condition, so nothing is posted and the server's own reason is rendered.
    // This is deliberately checked on the re-read and not on the cached prop —
    // the qualifying preview load lands seconds AFTER a preview tab opens, so
    // a cached `no_qualifying_preview` can lag the truth, and only an answer
    // from this instant is entitled to withhold the request.
    const refreshed = confirmation.data.lifecycle.capabilities.acceptCandidate;
    if (refreshed.state === "refused") {
      setVerdictRefusal(refreshed.reason);
      return;
    }

    signoff.mutate(
      {
        candidate: {
          candidateId: reviewed.candidateId,
          contentHash: reviewed.contentHash,
          basePackSha: reviewed.basePackSha,
        },
      },
      {
        onError(error) {
          setSignoffFailure(classifySignoffFailure(error));
        },
      },
    );
  }

  function handleRequestPublication() {
    if (!candidate || publication.isPending) {
      return;
    }
    setPublicationFailure(null);

    // One POST, with the session the browser already holds. The idempotency
    // key is minted at the first request for this candidate and kept for its
    // retries, so a replay after a transient failure returns the original
    // record instead of a second pull request. Nothing leaves this page and
    // nothing GitHub-shaped is asked of the clinician.
    const request = publicationRequestFor({
      projectId,
      candidate: {
        candidateId: candidate.candidateId,
        contentHash: candidate.contentHash,
        basePackSha: candidate.basePackSha,
      },
    });

    publication.mutate(request, {
      onError: recordPublicationFailure,
      onSuccess() {
        // The key outlives the request it was made for; leaving it behind
        // would make the next publication of this candidate replay this
        // record instead of publishing.
        clearIdempotencyKey(projectId, candidate.candidateId);
      },
    });
  }

  /**
   * The eligibility of the version that was READ, not of the project's latest.
   *
   * Read off `reviewed` rather than off the `candidate` prop since #435, and
   * for the same reason acceptance is: eligibility is a permanent, server-
   * derived property OF A CANDIDATE, so a sentence about it has to name the
   * candidate on screen. The two are the same value whenever the inspection is
   * current, and when they differ the prop is describing a version nobody here
   * has read. Nothing is lost by the narrowing: while `reviewed` is null,
   * acceptance is already withheld and says why.
   */
  const preview_only =
    reviewed && reviewed.eligibility.state === "preview_only"
      ? reviewed.eligibility
      : undefined;

  // `candidate` is `ProjectOverview.draft.latestCandidate`, selected by the
  // same database statement as the acceptance axis. The id comparison remains
  // fail-closed protection against malformed or stale props: acceptance of A
  // can never unlock publication of B.
  const currentCandidateAccepted =
    candidate !== null &&
    status.candidateAcceptance === "accepted" &&
    status.acceptedCandidateId === candidate.candidateId;
  const offersAcceptance =
    !currentCandidateAccepted &&
    (status.state === "none" || status.state === "action_needed");
  /**
   * Publication is offered exactly where the server says the write would be
   * accepted (#650's verdict; the retired `OFFERS_PUBLICATION` table above
   * explains what it replaces). The refused arms need no control here:
   * `acceptance_missing`'s recovery is the Accept flow, and an in-flight or
   * recorded publication is what the status line already says.
   */
  const offersPublication = publishVerdict.state === "permitted";

  /**
   * Whether this Project can have a publication row for approval to be about.
   *
   * `none` and `accepted` both precede the request, so the listing could only
   * answer empty — and asking anyway would put a request on the critical path
   * of every Overview mount for the majority of Projects. The two states below
   * are exactly the ones a publication row produces: `publication_requested`
   * covers every live rung, and each `action_needed` reason describes a
   * publication that exists and needs something.
   */
  const publicationExists =
    status.state === "publication_requested" ||
    status.state === "action_needed";

  /**
   * Why acceptance is withheld, in the order a clinician can act on.
   *
   * Staleness first, because it is the one that makes an otherwise perfectly
   * readable version the wrong thing to sign for — and the only one whose
   * recovery is a decision rather than a wait. `null` means the version on
   * screen was read and may be accepted; `preview_only` is a separate, older
   * refusal that keeps its own sentence below.
   */
  const acceptanceBlockedBy = stale
    ? "stale"
    : reviewed
      ? null
      : contents.isPending
        ? "loading"
        : "unreadable";

  if (detailsOnly) {
    return (
      <div className="space-y-3">
        <PublicationOutcomeNotice failure={publicationFailure} />
        <CandidateAcceptanceDetail
          status={status}
          currentCandidateId={candidate?.candidateId ?? null}
          viewerSubject={session.user.subject}
        />
        {inspectedCandidateId ? (
          <details>
            <summary className="cursor-pointer text-sm font-medium focus-visible:outline-2 focus-visible:outline-ring">
              Frozen files and technical evidence
            </summary>
            <div className="mt-3">
              <CandidateReview
                projectId={projectId}
                candidateId={inspectedCandidateId}
                contents={contents}
                acceptedCandidateId={status.acceptedCandidateId ?? null}
              />
            </div>
          </details>
        ) : (
          <p className="text-sm text-muted-foreground">
            No frozen test version yet.
          </p>
        )}
        {stale && currentCandidate ? (
          <Button
            variant="outline"
            onClick={() =>
              setInspectedCandidateId(currentCandidate.candidateId)
            }
          >
            Read the newer version
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <PublicationStatusView status={status} />
        {/*
          Beneath the status and OUTSIDE the candidate branch below, which is
          what makes it durable in the sense #436 asks for: it is keyed on the
          acceptance axis alone, so it survives every `state` — including the
          publication_requested that covers merged and PR-pending, and each
          action_needed recovery — and it reappears on a fresh mount because
          the projection, not this component, remembers.
        */}
        <CandidateAcceptanceDetail
          status={status}
          currentCandidateId={candidate?.candidateId ?? null}
          viewerSubject={session.user.subject}
        />
      </div>

      {/*
        A SECOND AXIS, not a second status. `PublicationStatus` renders one
        lifecycle line and never two, which is a rule about that projection —
        `action_needed` replacing the progress reading rather than sitting
        beside it. Approval is a different question with a different owner: the
        lifecycle axis is what the Editor recorded, and this is what it OBSERVED
        of a release owner acting in GitHub. The Promotion card already carries
        two independent axes for the same reason, and the two readings here
        agree rather than compete — a moved head reads `signoff_no_longer_current`
        above and `changed_head` here, which is one fact told from both ends.
      */}
      {publicationExists ? <ApprovalProgress projectId={projectId} /> : null}

      {!candidate ? (
        <p className="text-sm text-muted-foreground">
          There is no frozen version to accept yet. Review the clinical content,
          then freeze and preview a version.
        </p>
      ) : (
        <div className="space-y-3">
          {/*
            The server's own naming of the current version (#650/#651): the
            1-based revision number and the ratified validity window, both
            derived server-side — never a list index, never browser date-math.
            The plain line remains for the one honest gap: a lifecycle block
            describing a different candidate than the prop (a freeze landing
            between the two reads), where naming a number would misattribute it.
          */}
          {lifecycle.candidate &&
          lifecycle.candidate.identity.candidateId === candidate.candidateId ? (
            <p className="text-sm text-foreground">
              {`Revision ${lifecycle.candidate.revision.number}, frozen ${formatDateTime(candidate.createdAt)}.`}
              {lifecycle.candidate.validity.state === "valid"
                ? ` Valid until ${formatDateTime(lifecycle.candidate.validity.expiresAt)}.`
                : ` The validity window ended ${formatDateTime(lifecycle.candidate.validity.expiresAt)}.`}
              {supersessionSentence(lifecycle.candidate.freshness) === null
                ? null
                : ` ${supersessionSentence(lifecycle.candidate.freshness)}`}
            </p>
          ) : (
            <p className="text-sm text-foreground">
              Latest version frozen {formatDateTime(candidate.createdAt)}.
            </p>
          )}

          {/*
            The frozen version itself, before the action that signs for it
            (#435). It is rendered WITH the acceptance control rather than
            beside it, because "acceptance stays disabled until the exact
            candidate has loaded" is only expressible where the two are one
            block — and because ADR 0001 forbids a candidate identifier in a
            URL, so a route keyed by candidate was never available to put it on.
          */}
          {inspectedCandidateId ? (
            <CandidateReview
              projectId={projectId}
              candidateId={inspectedCandidateId}
              contents={contents}
              /*
                #436's audit row, carried into the review disclosure this
                ticket moved the exact identifiers to. It is read off the
                ACCEPTANCE axis — `status.acceptedCandidateId`, the same
                server-authoritative field `CandidateAcceptanceDetail` above
                reads — and never off the candidate being reviewed or off the
                Overview's latest: a superseded acceptance names an older
                version that appears nowhere else on the page, which is the
                whole reason #436 renders it. Sourcing it that way keeps it
                out of the cached-prop dependency this ticket removed.
              */
              acceptedCandidateId={status.acceptedCandidateId ?? null}
            />
          ) : null}

          {/*
            The moving "latest", made visible instead of silent. Rendered
            wherever the lane is — not only where acceptance is offered —
            because a clinician reading a superseded version should learn that
            from the page rather than from the button's absence.
          */}
          {stale && currentCandidate ? (
            <div
              role="alert"
              className="space-y-3 rounded-md border border-border bg-muted/40 p-3 text-sm text-foreground"
            >
              <p>{ACCEPTANCE_BLOCKED_COPY.stale}</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  setInspectedCandidateId(currentCandidate.candidateId)
                }
              >
                Read the newer version
              </Button>
            </div>
          ) : null}

          {offersAcceptance ? (
            <div className="space-y-2">
              <Button
                type="button"
                onClick={() => void handleAccept()}
                disabled={
                  Boolean(preview_only) ||
                  signoff.isPending ||
                  confirming ||
                  acceptanceBlockedBy !== null ||
                  hardVerdictRefusal !== null
                }
              >
                {signoff.isPending
                  ? "Accepting…"
                  : confirming
                    ? "Checking this is still the current version…"
                    : "Accept this version"}
              </Button>
              {/*
                Said beneath the disabled control rather than left to be
                inferred from it. The stale sentence is already on screen above
                in its own notice, with the one control that clears it, so it is
                not repeated here.
              */}
              {acceptanceBlockedBy !== null &&
              acceptanceBlockedBy !== "stale" &&
              !preview_only ? (
                <p className="text-sm text-muted-foreground">
                  {ACCEPTANCE_BLOCKED_COPY[acceptanceBlockedBy]}
                </p>
              ) : null}
              {preview_only ? (
                <p className="text-sm text-muted-foreground">
                  {ELIGIBILITY_COPY[preview_only.reason] ??
                    "This version cannot be accepted for publication yet."}
                </p>
              ) : null}
              {/*
                The hard verdict refusals (#650), said beneath the control they
                disable — the server's stable reason in its ratified sentence.
              */}
              {hardVerdictRefusal !== null && !preview_only ? (
                <p className="text-sm text-muted-foreground">
                  {lifecycleRefusalCopy(hardVerdictRefusal)}
                </p>
              ) : null}
              {/*
                The preview-first guidance, BEFORE the click (#651): the server
                already says acceptance would be refused for want of qualifying
                preview evidence, so the sentence a 409 used to deliver renders
                up front. The control stays live — see `hardVerdictRefusal` on
                why this reason must not disable — and the click-time re-read
                decides with fresh eyes.
              */}
              {verdictReason === "lifecycle.no_qualifying_preview" &&
              acceptanceBlockedBy === null &&
              !preview_only ? (
                <p className="text-sm text-muted-foreground">
                  {lifecycleRefusalCopy("lifecycle.no_qualifying_preview")}
                </p>
              ) : null}
              {/*
                The step the recovery sentence above does not mention, said here
                rather than left to be discovered: the server reports that
                accepting the current version is what unlocks requesting its
                publication. Verdict-derived (#650) — the same condition the
                retired policy table approximated.
              */}
              {!stale &&
              publishVerdict.state === "refused" &&
              publishVerdict.reason === "lifecycle.acceptance_missing" &&
              !preview_only ? (
                <p className="text-sm text-muted-foreground">
                  Confirm this version to request publication of it.
                </p>
              ) : null}
              {/*
                The acceptance that was pressed and refused before it was sent.
                Rendered above the service's own refusals because it is not one
                of them: no request was made, and the sentence has to say that
                rather than leave a clinician wondering what the Editor did with
                it.
              */}
              {freshnessUnconfirmed ? (
                <p role="alert" className="text-sm text-destructive">
                  {FRESHNESS_UNCONFIRMED_COPY}
                </p>
              ) : null}
              {/*
                The click-time verdict refusal: pressed, re-read, and the fresh
                answer said refused — so nothing was posted, and the sentence
                says both halves.
              */}
              {verdictRefusal !== null ? (
                <p role="alert" className="text-sm text-destructive">
                  {`This version was not accepted. ${lifecycleRefusalCopy(verdictRefusal)}`}
                </p>
              ) : null}
              {signoffFailure ? (
                <p role="alert" className="text-sm text-destructive">
                  {SIGNOFF_FAILURE_COPY[signoffFailure.kind]}
                </p>
              ) : null}
            </div>
          ) : null}

          {offersPublication ? (
            <div className="space-y-2">
              <Button
                type="button"
                onClick={() => handleRequestPublication()}
                disabled={publication.isPending}
              >
                {publication.isPending ? "Requesting…" : "Request publication"}
              </Button>
              <p className="text-sm text-muted-foreground">
                Requesting publication does not make this version live. A
                release owner decides when it is released.
              </p>
            </div>
          ) : null}

          {/*
            Outside the action block, so the refusal of a request whose
            control has since been withheld cannot vanish with it. It is still
            AT the action: nothing renders it but a refusal, and in every flow
            it sits directly beneath the control that was pressed.
          */}
          <PublicationOutcomeNotice failure={publicationFailure} />
        </div>
      )}
    </div>
  );
}

/**
 * What a refused publication says.
 *
 * A component rather than an inline fragment because it has two mount points:
 * beneath the action a clinician pressed, and in the details-only view of a
 * lane whose control is withheld. The publishing service's own unavailability
 * (`publishing_unavailable`, `publishing_refused`) renders here in the same
 * voice as every other refusal — there is no account to reconnect and no
 * recovery control to offer, because the identity that publishes is the
 * environment's, not the clinician's.
 */
function PublicationOutcomeNotice({
  failure,
}: {
  failure: PublicationFailure | null;
}) {
  if (failure === null) {
    return null;
  }
  return (
    <p role="alert" className="text-sm text-destructive">
      {PUBLICATION_FAILURE_COPY[failure.kind]}
    </p>
  );
}
